import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { apiWorker } from '../../api/worker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import toast from 'react-hot-toast';
import { Trophy, RotateCcw, Play, Volume2, VolumeX, Maximize2, Minimize2, Heart } from 'lucide-react';
import { GameLeaderboardScopeTabs } from './common/GameLeaderboardScopeTabs';

// ---------------------------------------------------------------------------
// Sound FX (Web Audio API Synthesizer)
// ---------------------------------------------------------------------------

const playSound = (type: 'jump' | 'coin' | 'hit' | 'powerup' | 'milestone' | 'gameover' | 'nearmiss' | 'combo') => {
  if (typeof window === 'undefined') return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;

    if (type === 'jump') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(260, now);
      osc.frequency.exponentialRampToValueAtTime(600, now + 0.1);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc.start(now); osc.stop(now + 0.12);
    } else if (type === 'coin') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(988, now);
      osc.frequency.setValueAtTime(1319, now + 0.06);
      gain.gain.setValueAtTime(0.07, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc.start(now); osc.stop(now + 0.12);
    } else if (type === 'hit') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(50, now + 0.25);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now); osc.stop(now + 0.25);
    } else if (type === 'powerup') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523, now);
      osc.frequency.setValueAtTime(659, now + 0.07);
      osc.frequency.setValueAtTime(784, now + 0.14);
      osc.frequency.setValueAtTime(1047, now + 0.21);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc.start(now); osc.stop(now + 0.35);
    } else if (type === 'milestone') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.setValueAtTime(554, now + 0.08);
      osc.frequency.setValueAtTime(659, now + 0.16);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      osc.start(now); osc.stop(now + 0.3);
    } else if (type === 'gameover') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(110, now + 0.5);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc.start(now); osc.stop(now + 0.5);
    } else if (type === 'nearmiss') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, now);
      osc.frequency.exponentialRampToValueAtTime(1400, now + 0.08);
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc.start(now); osc.stop(now + 0.12);
    } else if (type === 'combo') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(660, now);
      osc.frequency.setValueAtTime(880, now + 0.05);
      osc.frequency.setValueAtTime(1100, now + 0.1);
      gain.gain.setValueAtTime(0.07, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
      osc.start(now); osc.stop(now + 0.18);
    }
  } catch {
    // Ignore audio context autoplay blocks
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

async function tryLockLandscape(): Promise<boolean> {
  try {
    const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    if (so?.lock) { await so.lock('landscape'); return true; }
  } catch { /* ignore */ }
  return false;
}

function tryUnlockOrientation() {
  try {
    const so = screen.orientation as ScreenOrientation & { unlock?: () => void };
    if (so?.unlock) so.unlock();
  } catch { /* ignore */ }
}

function useIsPortraitMobile(): boolean {
  const [isPortrait, setIsPortrait] = useState(false);
  useEffect(() => {
    const check = () => {
      setIsPortrait(isMobileDevice() && window.innerHeight > window.innerWidth);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return isPortrait;
}

// ---------------------------------------------------------------------------
// Game Constants
// ---------------------------------------------------------------------------

const CANVAS_W = 800;
const CANVAS_H = 400;
const GROUND_Y = 340;
const GRAVITY = 0.65;
const JUMP_FORCE = -12.5;
const DOUBLE_JUMP_FORCE = -10;
const PLAYER_W = 40;
const PLAYER_H = 48;
const BASE_SPEED = 4;
const MAX_SPEED = 12;
const SPEED_INCREMENT = 0.0008;
const NEAR_MISS_THRESHOLD = 8;

// Obstacle/collectible types
interface Obstacle {
  x: number; y: number; w: number; h: number;
  type: 'paperwork' | 'meeting' | 'audit' | 'email' | 'redtape';
  passed: boolean;
  nearMissChecked: boolean;
}

interface Collectible {
  x: number; y: number;
  type: 'coin' | 'coffee' | 'shield' | 'magnet';
  collected: boolean;
  floatOffset: number;
}

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  color: string; size: number;
}

interface FloatingText {
  x: number; y: number;
  text: string; color: string;
  life: number; maxLife: number;
  vy: number;
}

interface BackgroundElement {
  x: number; y: number;
  type: 'building' | 'tree' | 'cloud' | 'lamppost';
  w: number; h: number;
  speed: number; color: string;
  windowSeed: number;
  hasAntenna: boolean;
  hasAC: boolean;
  roofType: number; // 0=flat, 1=pointed, 2=stepped
}

interface ForegroundProp {
  x: number;
  type: 'plant' | 'cooler' | 'bin';
  speed: number;
}

interface LeaderboardEntry {
  playerName: string;
  score: number;
  distance?: number;
  date?: string;
  metadata?: Record<string, any>;
}

// Obstacle configs
const OBSTACLE_CONFIGS = {
  paperwork: { w: 34, h: 42 },
  meeting:   { w: 44, h: 38 },
  audit:     { w: 38, h: 44 },
  email:     { w: 32, h: 30 },
  redtape:   { w: 52, h: 22 },
};

const COLLECTIBLE_CONFIGS = {
  coin:   { label: 'Star Coin', points: 10 },
  coffee: { label: 'Coffee Boost', points: 5 },
  shield: { label: 'Shield', points: 0 },
  magnet: { label: 'Coin Magnet', points: 0 },
};

const OBSTACLE_TYPES: Obstacle['type'][] = ['paperwork', 'meeting', 'audit', 'email', 'redtape'];
const COLLECTIBLE_TYPES: Collectible['type'][] = ['coin', 'coin', 'coin', 'coin', 'coffee', 'shield', 'magnet'];

// ---------------------------------------------------------------------------
// Drawing Helpers
// ---------------------------------------------------------------------------

function drawObstacle(ctx: CanvasRenderingContext2D, obs: Obstacle) {
  ctx.save();
  const cx = obs.x + obs.w / 2;
  const cy = obs.y + obs.h / 2;

  if (obs.type === 'paperwork') {
    // Stack of papers
    const stackColors = ['#f0ebe0', '#e8e3d8', '#f5f0e5'];
    for (let i = 2; i >= 0; i--) {
      ctx.fillStyle = stackColors[i];
      ctx.fillRect(obs.x + i * 2 - 2, obs.y + i * 3, obs.w - 4, obs.h - 8);
      ctx.strokeStyle = 'rgba(0,0,0,0.15)';
      ctx.lineWidth = 1;
      ctx.strokeRect(obs.x + i * 2 - 2, obs.y + i * 3, obs.w - 4, obs.h - 8);
    }
    // Text lines on top sheet
    ctx.strokeStyle = 'rgba(100,80,60,0.25)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(obs.x + 4, obs.y + 8 + i * 6);
      ctx.lineTo(obs.x + obs.w - 10 - Math.random() * 8, obs.y + 8 + i * 6);
      ctx.stroke();
    }
    // Red clip at top
    ctx.fillStyle = '#cc3333';
    ctx.fillRect(obs.x + obs.w / 2 - 6, obs.y - 3, 12, 8);
    ctx.fillStyle = '#aa2222';
    ctx.fillRect(obs.x + obs.w / 2 - 4, obs.y - 2, 8, 5);
  } else if (obs.type === 'meeting') {
    // Calendar card
    ctx.fillStyle = '#fff';
    ctx.fillRect(obs.x, obs.y, obs.w, obs.h);
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(obs.x, obs.y, obs.w, obs.h);
    // Red header bar
    ctx.fillStyle = '#dd3333';
    ctx.fillRect(obs.x, obs.y, obs.w, 10);
    // Calendar grid lines
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) {
        ctx.strokeRect(obs.x + 3 + c * 9, obs.y + 14 + r * 7, 8, 6);
      }
    }
    // Red notification dot
    ctx.fillStyle = '#ff0000';
    ctx.beginPath();
    ctx.arc(obs.x + obs.w - 5, obs.y + 5, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 5px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('3', obs.x + obs.w - 5, obs.y + 5.5);
  } else if (obs.type === 'audit') {
    // Magnifying glass
    const handle = { x: cx - 6, y: cy + 10 };
    // Handle
    ctx.strokeStyle = '#8B7355';
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(handle.x, handle.y);
    ctx.lineTo(handle.x - 8, handle.y + 12);
    ctx.stroke();
    // Glass ring
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(cx, cy - 2, 13, 0, Math.PI * 2);
    ctx.stroke();
    // Glass fill
    ctx.fillStyle = 'rgba(180,220,255,0.35)';
    ctx.beginPath();
    ctx.arc(cx, cy - 2, 12, 0, Math.PI * 2);
    ctx.fill();
    // Glint
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx - 4, cy - 6, 4, -0.5, 1);
    ctx.stroke();
  } else if (obs.type === 'email') {
    // Envelope
    ctx.fillStyle = '#f0ebe0';
    ctx.fillRect(obs.x, obs.y, obs.w, obs.h);
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(obs.x, obs.y, obs.w, obs.h);
    // Flap (triangle)
    ctx.fillStyle = '#e0dbd0';
    ctx.beginPath();
    ctx.moveTo(obs.x, obs.y);
    ctx.lineTo(cx, cy + 2);
    ctx.lineTo(obs.x + obs.w, obs.y);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.15)';
    ctx.stroke();
    // Notification badge
    ctx.fillStyle = '#ff2222';
    ctx.beginPath();
    ctx.arc(obs.x + obs.w - 2, obs.y + 2, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 7px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('99', obs.x + obs.w - 2, obs.y + 3);
  } else if (obs.type === 'redtape') {
    // Striped barrier tape
    const stripeW = 8;
    const numStripes = Math.ceil(obs.w / stripeW);
    for (let i = 0; i < numStripes; i++) {
      ctx.fillStyle = i % 2 === 0 ? '#cc2200' : '#ffffff';
      ctx.fillRect(obs.x + i * stripeW, obs.y, stripeW, obs.h);
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(obs.x, obs.y, obs.w, obs.h);
    // Posts at ends
    ctx.fillStyle = '#888';
    ctx.fillRect(obs.x - 2, obs.y - 8, 4, obs.h + 8);
    ctx.fillRect(obs.x + obs.w - 2, obs.y - 8, 4, obs.h + 8);
  }

  // Danger glow
  ctx.shadowColor = 'rgba(255,50,0,0.25)';
  ctx.shadowBlur = 10;
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  ctx.restore();
}

function drawCollectible(ctx: CanvasRenderingContext2D, col: Collectible, frameCount: number) {
  const floatY = col.y + Math.sin(col.floatOffset) * 5;
  ctx.save();

  if (col.type === 'coin') {
    // Golden star coin with rotating sparkle ring
    const sparkleAngle = frameCount * 0.08;
    // Glow
    ctx.shadowColor = 'rgba(255,214,0,0.5)';
    ctx.shadowBlur = 10;
    // Coin body
    ctx.fillStyle = '#FFD600';
    ctx.beginPath();
    ctx.arc(col.x, floatY, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    // Inner detail
    ctx.fillStyle = '#ffec44';
    ctx.beginPath();
    ctx.arc(col.x, floatY, 7, 0, Math.PI * 2);
    ctx.fill();
    // Star shape
    ctx.fillStyle = '#FFD600';
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = sparkleAngle + (i * Math.PI * 2) / 5 - Math.PI / 2;
      const r = i % 2 === 0 ? 6 : 3;
      const method = i === 0 ? 'moveTo' : 'lineTo';
      const outerA = sparkleAngle + (i * Math.PI * 2) / 5 - Math.PI / 2;
      ctx[method](col.x + Math.cos(outerA) * 6, floatY + Math.sin(outerA) * 6);
      const innerA = sparkleAngle + ((i + 0.5) * Math.PI * 2) / 5 - Math.PI / 2;
      ctx.lineTo(col.x + Math.cos(innerA) * 3, floatY + Math.sin(innerA) * 3);
    }
    ctx.closePath();
    ctx.fill();
    // Sparkle dots orbiting
    for (let i = 0; i < 3; i++) {
      const sa = sparkleAngle * 1.5 + i * (Math.PI * 2 / 3);
      const sx = col.x + Math.cos(sa) * 14;
      const sy = floatY + Math.sin(sa) * 14;
      ctx.fillStyle = 'rgba(255,255,200,0.6)';
      ctx.fillRect(sx - 1, sy - 1, 2, 2);
    }
  } else if (col.type === 'coffee') {
    // Coffee cup with steam
    ctx.shadowColor = 'rgba(139,69,19,0.4)';
    ctx.shadowBlur = 8;
    // Cup body
    ctx.fillStyle = '#f5f0e5';
    ctx.fillRect(col.x - 7, floatY - 5, 14, 16);
    ctx.strokeStyle = '#c5b5a0';
    ctx.lineWidth = 1;
    ctx.strokeRect(col.x - 7, floatY - 5, 14, 16);
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    // Coffee inside
    ctx.fillStyle = '#4a2810';
    ctx.fillRect(col.x - 5, floatY - 3, 10, 6);
    // Handle
    ctx.strokeStyle = '#c5b5a0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(col.x + 10, floatY + 2, 4, -1.2, 1.2);
    ctx.stroke();
    // Brown band
    ctx.fillStyle = '#8B5E3C';
    ctx.fillRect(col.x - 7, floatY + 4, 14, 4);
    // Steam particles
    ctx.strokeStyle = 'rgba(200,200,200,0.35)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const sx = col.x - 4 + i * 4;
      const steamOffset = Math.sin(frameCount * 0.06 + i * 2) * 2;
      ctx.beginPath();
      ctx.moveTo(sx, floatY - 6);
      ctx.quadraticCurveTo(sx + steamOffset, floatY - 14, sx - steamOffset, floatY - 20);
      ctx.stroke();
    }
  } else if (col.type === 'shield') {
    // Shield with pulsing glow
    const pulse = 0.5 + Math.sin(frameCount * 0.1) * 0.3;
    ctx.shadowColor = `rgba(0,68,255,${pulse})`;
    ctx.shadowBlur = 12;
    // Shield shape
    ctx.fillStyle = '#0044FF';
    ctx.beginPath();
    ctx.moveTo(col.x, floatY - 12);
    ctx.lineTo(col.x + 10, floatY - 6);
    ctx.lineTo(col.x + 10, floatY + 4);
    ctx.quadraticCurveTo(col.x, floatY + 14, col.x, floatY + 14);
    ctx.quadraticCurveTo(col.x, floatY + 14, col.x - 10, floatY + 4);
    ctx.lineTo(col.x - 10, floatY - 6);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    // Inner highlight
    ctx.fillStyle = 'rgba(100,160,255,0.4)';
    ctx.beginPath();
    ctx.moveTo(col.x, floatY - 8);
    ctx.lineTo(col.x + 6, floatY - 4);
    ctx.lineTo(col.x + 6, floatY + 2);
    ctx.quadraticCurveTo(col.x, floatY + 9, col.x, floatY + 9);
    ctx.quadraticCurveTo(col.x, floatY + 9, col.x - 6, floatY + 2);
    ctx.lineTo(col.x - 6, floatY - 4);
    ctx.closePath();
    ctx.fill();
    // Cross/star emblem
    ctx.fillStyle = '#fff';
    ctx.fillRect(col.x - 1, floatY - 4, 2, 8);
    ctx.fillRect(col.x - 4, floatY - 1, 8, 2);
  } else if (col.type === 'magnet') {
    // Horseshoe magnet with field lines
    const pulse = Math.sin(frameCount * 0.08) * 4;
    ctx.shadowColor = 'rgba(255,0,255,0.4)';
    ctx.shadowBlur = 10;
    // U-shape
    ctx.strokeStyle = '#cc0066';
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(col.x, floatY, 8, 0, Math.PI);
    ctx.stroke();
    // Poles
    ctx.fillStyle = '#cc0066';
    ctx.fillRect(col.x - 12, floatY - 2, 5, 10);
    ctx.fillRect(col.x + 7, floatY - 2, 5, 10);
    // Pole tips (red/blue)
    ctx.fillStyle = '#ff4444';
    ctx.fillRect(col.x - 12, floatY + 5, 5, 4);
    ctx.fillStyle = '#4444ff';
    ctx.fillRect(col.x + 7, floatY + 5, 5, 4);
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    // Field lines
    ctx.strokeStyle = `rgba(255,0,255,${0.15 + Math.abs(Math.sin(frameCount * 0.06)) * 0.15})`;
    ctx.lineWidth = 0.5;
    ctx.setLineDash([2, 3]);
    for (let i = 0; i < 3; i++) {
      const r = 16 + i * 5 + pulse * 0.5;
      ctx.beginPath();
      ctx.arc(col.x, floatY, r, -0.2, Math.PI + 0.2);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  ctx.restore();
}

function drawPlayer(
  ctx: CanvasRenderingContext2D,
  px: number, py: number,
  playerH: number, ducking: boolean,
  isGrounded: boolean, runFrame: number, vy: number,
  coffeeTimer: number, frameCount: number,
  squashStretch: { sx: number; sy: number }
) {
  ctx.save();
  const cx = px + PLAYER_W / 2;
  const cy = py + playerH / 2;

  // Apply squash/stretch
  ctx.translate(cx, py + playerH);
  ctx.scale(squashStretch.sx, squashStretch.sy);
  ctx.translate(-cx, -(py + playerH));

  const bodyColor = coffeeTimer > 0 ? '#FF8C00' : '#0044FF';
  const darkBody = coffeeTimer > 0 ? '#cc6f00' : '#003acc';

  if (ducking) {
    // Ducking pose: compressed, sliding
    // Body (wide and low)
    ctx.fillStyle = bodyColor;
    ctx.fillRect(px + 4, py + playerH - 18, PLAYER_W - 8, 12);
    // Head (tucked)
    ctx.fillStyle = '#FFD8B1';
    ctx.beginPath();
    ctx.arc(px + PLAYER_W / 2, py + playerH - 20, 8, 0, Math.PI * 2);
    ctx.fill();
    // Hair
    ctx.fillStyle = '#3d2817';
    ctx.beginPath();
    ctx.arc(px + PLAYER_W / 2, py + playerH - 22, 7, Math.PI, Math.PI * 2);
    ctx.fill();
    // Eyes (squinting)
    ctx.fillStyle = '#000';
    ctx.fillRect(px + PLAYER_W / 2 + 1, py + playerH - 20, 3, 2);
    // Briefcase (dragging)
    ctx.fillStyle = '#6B4226';
    ctx.fillRect(px + PLAYER_W - 6, py + playerH - 12, 8, 6);
    ctx.fillStyle = '#d4a574';
    ctx.fillRect(px + PLAYER_W - 4, py + playerH - 13, 4, 2);
    // Legs (tucked)
    ctx.fillStyle = '#222';
    ctx.fillRect(px + 6, py + playerH - 6, PLAYER_W - 12, 6);
  } else {
    // Pants
    ctx.fillStyle = '#222';
    const legSpread = isGrounded ? Math.sin(runFrame * Math.PI / 2) * 6 : 0;
    // Left leg
    ctx.fillRect(px + 10, py + playerH - 16, 7, 16);
    // Right leg
    ctx.fillRect(px + PLAYER_W - 17 + legSpread / 2, py + playerH - 14, 7, 14);
    // Shoes
    ctx.fillStyle = '#111';
    ctx.fillRect(px + 8, py + playerH - 4, 10, 4);
    ctx.fillRect(px + PLAYER_W - 18 + legSpread / 2, py + playerH - 4, 10, 4);

    // Body / shirt
    ctx.fillStyle = bodyColor;
    ctx.fillRect(px + 8, py + 14, PLAYER_W - 16, playerH - 30);
    // Shirt collar
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(px + 14, py + 14);
    ctx.lineTo(px + PLAYER_W / 2, py + 20);
    ctx.lineTo(px + PLAYER_W - 14, py + 14);
    ctx.closePath();
    ctx.fill();
    // Tie
    ctx.fillStyle = '#FFD600';
    ctx.beginPath();
    ctx.moveTo(px + PLAYER_W / 2, py + 17);
    ctx.lineTo(px + PLAYER_W / 2 + 3, py + 22);
    ctx.lineTo(px + PLAYER_W / 2, py + 32);
    ctx.lineTo(px + PLAYER_W / 2 - 3, py + 22);
    ctx.closePath();
    ctx.fill();

    // Arms
    const armSwing = isGrounded ? Math.sin(runFrame * Math.PI / 2) * 8 : -4;
    ctx.fillStyle = bodyColor;
    // Left arm
    ctx.save();
    ctx.translate(px + 7, py + 18);
    ctx.rotate(-0.2 + armSwing * 0.03);
    ctx.fillRect(-3, 0, 6, 14);
    // Hand
    ctx.fillStyle = '#FFD8B1';
    ctx.fillRect(-2, 12, 5, 5);
    ctx.restore();
    // Right arm (holding briefcase)
    ctx.save();
    ctx.translate(px + PLAYER_W - 7, py + 18);
    ctx.rotate(0.2 - armSwing * 0.03);
    ctx.fillStyle = bodyColor;
    ctx.fillRect(-3, 0, 6, 14);
    ctx.fillStyle = '#FFD8B1';
    ctx.fillRect(-2, 12, 5, 5);
    // Briefcase
    ctx.fillStyle = '#6B4226';
    ctx.fillRect(-1, 16, 10, 8);
    ctx.fillStyle = '#d4a574';
    ctx.fillRect(1, 15, 6, 2);
    ctx.fillStyle = '#FFD600';
    ctx.fillRect(3, 17, 3, 2);
    ctx.restore();

    // Head
    ctx.fillStyle = '#FFD8B1';
    ctx.beginPath();
    ctx.arc(px + PLAYER_W / 2, py + 10, 10, 0, Math.PI * 2);
    ctx.fill();

    // Hair
    ctx.fillStyle = '#3d2817';
    ctx.beginPath();
    ctx.arc(px + PLAYER_W / 2, py + 8, 9, Math.PI + 0.3, Math.PI * 2 - 0.3);
    ctx.fill();
    // Side hair
    ctx.fillRect(px + PLAYER_W / 2 - 10, py + 5, 3, 6);

    // Eyes
    ctx.fillStyle = '#000';
    ctx.fillRect(px + PLAYER_W / 2 + 2, py + 8, 3, 3);
    // Eyebrow
    ctx.fillRect(px + PLAYER_W / 2 + 1, py + 6, 5, 1);
    // Mouth (determined expression when jumping, neutral otherwise)
    if (!isGrounded) {
      ctx.fillRect(px + PLAYER_W / 2 + 1, py + 14, 4, 1);
    } else {
      ctx.fillStyle = '#a06050';
      ctx.fillRect(px + PLAYER_W / 2 + 2, py + 14, 3, 1);
    }
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function OrgRunGame() {
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const gameStateRef = useRef<'menu' | 'playing' | 'gameover'>('menu');

  // Player state refs
  const playerRef = useRef({
    x: 100,
    y: GROUND_Y - PLAYER_H,
    vy: 0,
    isGrounded: true,
    jumpsLeft: 2,
    ducking: false,
    shieldTimer: 0,
    magnetTimer: 0,
    coffeeTimer: 0,
    invincibleTimer: 0,
    runFrame: 0,
    frameCounter: 0,
    squashStretch: { sx: 1, sy: 1 },
    wasGrounded: true,
  });

  const gameDataRef = useRef({
    speed: BASE_SPEED,
    score: 0,
    distance: 0,
    coins: 0,
    lives: 3,
    combo: 0,
    comboTimer: 0,
    obstacles: [] as Obstacle[],
    collectibles: [] as Collectible[],
    particles: [] as Particle[],
    floatingTexts: [] as FloatingText[],
    bgElements: [] as BackgroundElement[],
    fgProps: [] as ForegroundProp[],
    nextObstacleSpawn: 120,
    nextCollectibleSpawn: 80,
    frameCount: 0,
    lastMilestone: 0,
    groundOffset: 0,
    screenShake: 0,
    flashTimer: 0,
  });

  const inputRef = useRef({ jumpPressed: false, duckPressed: false });

  // React state for UI
  const [gamePhase, setGamePhase] = useState<'menu' | 'playing' | 'gameover'>('menu');
  const [score, setScore] = useState(0);
  const [distance, setDistance] = useState(0);
  const [lives, setLives] = useState(3);
  const [coins, setCoins] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { isSignedIn, user } = useCurrentUser();
  const { node, isGlobal } = useTenant();
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const nodeName = hasActiveNode ? (node?.name || 'Organization') : '';
  const [leaderboardScope, setLeaderboardScope] = useState<'world' | 'node'>('world');
  const [playerName, setPlayerName] = useState(() => {
    if (user?.username || user?.fullName || user?.firstName) {
      return user.username || user.fullName || user.firstName;
    }
    if (typeof window !== 'undefined') return localStorage.getItem('orgrun_player_name') || '';
    return '';
  });
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [scoreSavedToGlobal, setScoreSavedToGlobal] = useState(false);
  const isPortraitMobile = useIsPortraitMobile();
  const mutedRef = useRef(false);

  // Fill playerName from the auth user once it's available (render-phase adjustment;
  // setState during render with a previous-value guard is the sanctioned pattern).
  const [playerNameUser, setPlayerNameUser] = useState(user);
  if (user && !playerName && user !== playerNameUser) {
    setPlayerNameUser(user);
    setPlayerName(user.username || user.fullName || user.firstName || '');
  }

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Leaderboard queries
  const { data: leaderboardRaw } = useQuery<any>({
    queryKey: ['orgrun-leaderboard', leaderboardScope, leaderboardScope === 'node' ? node?.id : 'world'],
    queryFn: async () => {
      try { 
        const params: any = {};
        if (leaderboardScope === 'node' && hasActiveNode && node?.id) {
          params.nodeId = node.id;
        }
        const res = await apiWorker.get('/api/games/orgrun/leaderboard', { params, silent: true });
        return res.data;
      }
      catch { return []; }
    },
    staleTime: 30000,
  });

  const leaderboardData: LeaderboardEntry[] = Array.isArray(leaderboardRaw)
    ? leaderboardRaw
    : Array.isArray(leaderboardRaw?.content)
      ? leaderboardRaw.content
      : Array.isArray(leaderboardRaw?.data)
        ? leaderboardRaw.data
        : [];

  const submitScoreMutation = useMutation({
    mutationFn: async (entry: LeaderboardEntry) => {
      await apiWorker.post('/api/games/orgrun/leaderboard', {
        playerName: entry.playerName,
        score: entry.score,
        game: 'orgrun',
        nodeId: hasActiveNode ? node?.id : undefined,
        metadata: {
          distance: entry.distance,
          ...(entry.metadata || {})
        }
      }, { silent: true });
    },
    onSuccess: () => {
      setScoreSavedToGlobal(true);
      toast.success('Score saved to global leaderboard!');
      queryClient.invalidateQueries({ queryKey: ['orgrun-leaderboard'] });
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        toast.error('Sign in required to save score to global leaderboard.', { id: 'orgrun-401' });
      } else {
        toast.error('Could not connect to leaderboard server.');
      }
    },
  });

  const [localLeaderboard, setLocalLeaderboard] = useState<LeaderboardEntry[]>(() => {
    if (typeof window === 'undefined') return [];
    try { return JSON.parse(localStorage.getItem('orgrun_leaderboard') || '[]'); }
    catch { return []; }
  });

  const leaderboard = (leaderboardData && leaderboardData.length > 0) ? leaderboardData : localLeaderboard;

  // ---------------------------------------------------------------------------
  // Background generation
  // ---------------------------------------------------------------------------

  const generateBackground = useCallback(() => {
    const elements: BackgroundElement[] = [];
    // Far buildings (parallax layer 1)
    for (let i = 0; i < 10; i++) {
      const h = 60 + Math.random() * 140;
      elements.push({
        x: i * 100,
        y: GROUND_Y - h,
        type: 'building',
        w: 45 + Math.random() * 45,
        h,
        speed: 0.2,
        color: `hsl(${220 + Math.random() * 20}, ${12 + Math.random() * 8}%, ${18 + Math.random() * 12}%)`,
        windowSeed: Math.floor(Math.random() * 10000),
        hasAntenna: Math.random() > 0.6,
        hasAC: Math.random() > 0.5,
        roofType: Math.floor(Math.random() * 3),
      });
    }
    // Clouds (parallax layer 0.5)
    for (let i = 0; i < 6; i++) {
      elements.push({
        x: i * 180 + Math.random() * 80,
        y: 15 + Math.random() * 50,
        type: 'cloud',
        w: 50 + Math.random() * 60,
        h: 18 + Math.random() * 14,
        speed: 0.08,
        color: `rgba(255,255,255,${0.06 + Math.random() * 0.06})`,
        windowSeed: 0, hasAntenna: false, hasAC: false, roofType: 0,
      });
    }
    return elements;
  }, []);

  const generateForegroundProps = useCallback((): ForegroundProp[] => {
    const props: ForegroundProp[] = [];
    for (let i = 0; i < 4; i++) {
      props.push({
        x: i * 250 + Math.random() * 100,
        type: (['plant', 'cooler', 'bin'] as const)[Math.floor(Math.random() * 3)],
        speed: 0.85,
      });
    }
    return props;
  }, []);

  // ---------------------------------------------------------------------------
  // Spawn helpers
  // ---------------------------------------------------------------------------

  const spawnObstacle = useCallback(() => {
    const gd = gameDataRef.current;
    const type = OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)];
    const config = OBSTACLE_CONFIGS[type];
    let y = GROUND_Y - config.h;

    if ((type === 'email' || type === 'meeting') && Math.random() < 0.4) {
      y = GROUND_Y - config.h - 40 - Math.random() * 50;
    }
    if (type === 'redtape') y = GROUND_Y - config.h;

    gd.obstacles.push({ x: CANVAS_W + 20, y, w: config.w, h: config.h, type, passed: false, nearMissChecked: false });

    const minGap = Math.max(40, 90 - gd.speed * 4);
    const maxGap = Math.max(60, 140 - gd.speed * 5);
    gd.nextObstacleSpawn = minGap + Math.random() * (maxGap - minGap);
  }, []);

  const spawnCollectible = useCallback(() => {
    const gd = gameDataRef.current;
    const type = COLLECTIBLE_TYPES[Math.floor(Math.random() * COLLECTIBLE_TYPES.length)];
    const y = GROUND_Y - 30 - Math.random() * 80;
    gd.collectibles.push({ x: CANVAS_W + 20, y, type, collected: false, floatOffset: Math.random() * Math.PI * 2 });
    gd.nextCollectibleSpawn = 50 + Math.random() * 70;
  }, []);

  const spawnParticles = useCallback((x: number, y: number, color: string, count: number) => {
    const gd = gameDataRef.current;
    for (let i = 0; i < count; i++) {
      gd.particles.push({
        x, y,
        vx: (Math.random() - 0.5) * 6,
        vy: (Math.random() - 1) * 5,
        life: 30 + Math.random() * 20,
        maxLife: 50,
        color,
        size: 2 + Math.random() * 3,
      });
    }
  }, []);

  const spawnFloatingText = useCallback((x: number, y: number, text: string, color: string) => {
    gameDataRef.current.floatingTexts.push({
      x, y, text, color,
      life: 60, maxLife: 60,
      vy: -1.5,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Game loop
  // ---------------------------------------------------------------------------

  const gameLoopRef = useRef<() => void>(() => {});

  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = playerRef.current;
    const gd = gameDataRef.current;
    const inp = inputRef.current;

    if (gameStateRef.current !== 'playing') return;

    gd.frameCount++;

    // --- Input processing ---
    if (inp.jumpPressed && p.jumpsLeft > 0) {
      if (p.isGrounded) {
        p.vy = JUMP_FORCE;
        p.isGrounded = false;
        p.jumpsLeft = 1;
        // Squash before launch
        p.squashStretch = { sx: 1.2, sy: 0.7 };
      } else {
        p.vy = DOUBLE_JUMP_FORCE;
        p.jumpsLeft = 0;
      }
      if (!mutedRef.current) playSound('jump');
      spawnParticles(p.x + PLAYER_W / 2, p.y + PLAYER_H, '#aaa', 5);
      inp.jumpPressed = false;
    }

    p.ducking = inp.duckPressed && p.isGrounded;

    // --- Physics ---
    p.vy += GRAVITY;
    p.y += p.vy;

    p.wasGrounded = p.isGrounded;

    if (p.y >= GROUND_Y - PLAYER_H) {
      p.y = GROUND_Y - PLAYER_H;
      if (!p.isGrounded) {
        // Landing impact
        p.squashStretch = { sx: 1.3, sy: 0.6 };
        spawnParticles(p.x + PLAYER_W / 2, GROUND_Y, '#666', 4);
      }
      p.vy = 0;
      p.isGrounded = true;
      p.jumpsLeft = 2;
    }

    // Lerp squash/stretch back to normal
    p.squashStretch.sx += (1 - p.squashStretch.sx) * 0.15;
    p.squashStretch.sy += (1 - p.squashStretch.sy) * 0.15;

    // Speed ramp
    const coffeeMultiplier = p.coffeeTimer > 0 ? 1.4 : 1;
    gd.speed = Math.min(MAX_SPEED, BASE_SPEED + gd.frameCount * SPEED_INCREMENT) * coffeeMultiplier;
    gd.distance += gd.speed;
    gd.score = Math.floor(gd.distance / 10);

    // Timers
    if (p.shieldTimer > 0) p.shieldTimer--;
    if (p.magnetTimer > 0) p.magnetTimer--;
    if (p.coffeeTimer > 0) p.coffeeTimer--;
    if (p.invincibleTimer > 0) p.invincibleTimer--;
    if (gd.screenShake > 0) gd.screenShake--;
    if (gd.flashTimer > 0) gd.flashTimer--;
    if (gd.comboTimer > 0) {
      gd.comboTimer--;
      if (gd.comboTimer <= 0) gd.combo = 0;
    }

    // Animation frame
    p.frameCounter++;
    if (p.frameCounter % 5 === 0) p.runFrame = (p.runFrame + 1) % 4;

    // Milestones
    const currentMilestone = Math.floor(gd.score / 500);
    if (currentMilestone > gd.lastMilestone) {
      gd.lastMilestone = currentMilestone;
      if (!mutedRef.current) playSound('milestone');
      spawnFloatingText(CANVAS_W / 2, 80, `${currentMilestone * 500}m!`, '#FFD600');
    }

    // Ground scroll
    gd.groundOffset = (gd.groundOffset + gd.speed) % 40;

    // Run dust particles (continuous when grounded)
    if (p.isGrounded && !p.ducking && gd.frameCount % 4 === 0) {
      gd.particles.push({
        x: p.x + PLAYER_W / 2 - 5 + Math.random() * 10,
        y: GROUND_Y - 2,
        vx: -gd.speed * 0.3 + (Math.random() - 0.5) * 0.5,
        vy: -(Math.random() * 1.5),
        life: 15 + Math.random() * 10,
        maxLife: 25,
        color: '#7a7060',
        size: 2 + Math.random() * 2,
      });
    }

    // --- Spawn ---
    gd.nextObstacleSpawn--;
    if (gd.nextObstacleSpawn <= 0) spawnObstacle();

    gd.nextCollectibleSpawn--;
    if (gd.nextCollectibleSpawn <= 0) spawnCollectible();

    // --- Update obstacles ---
    for (let oi = 0; oi < gd.obstacles.length; oi++) {
      const obs = gd.obstacles[oi];
      obs.x -= gd.speed;
      if (!obs.passed && obs.x + obs.w < p.x) {
        obs.passed = true;
        // Combo increment for dodging
        gd.combo++;
        gd.comboTimer = 120; // 2 seconds to keep combo alive
        if (gd.combo >= 3 && gd.combo % 3 === 0) {
          const bonus = gd.combo * 5;
          gd.score += bonus;
          spawnFloatingText(p.x + PLAYER_W, p.y - 20, `${gd.combo}x COMBO! +${bonus}`, '#ff44ff');
          if (!mutedRef.current) playSound('combo');
        }
      }
      // Near-miss check
      if (!obs.nearMissChecked && obs.x + obs.w < p.x + 4 && obs.x + obs.w > p.x - NEAR_MISS_THRESHOLD) {
        const pH = p.ducking ? PLAYER_H * 0.5 : PLAYER_H;
        const pY = p.ducking ? GROUND_Y - pH : p.y;
        const vertClose = (pY < obs.y + obs.h + NEAR_MISS_THRESHOLD) && (pY + pH > obs.y - NEAR_MISS_THRESHOLD);
        if (vertClose && !obs.passed) {
          obs.nearMissChecked = true;
          gd.score += 25;
          spawnFloatingText(p.x + PLAYER_W + 10, p.y, 'CLOSE CALL! +25', '#44ffaa');
          spawnParticles(p.x + PLAYER_W, p.y + pH / 2, '#44ffaa', 6);
          if (!mutedRef.current) playSound('nearmiss');
        }
      }
      if (obs.x + obs.w < p.x - NEAR_MISS_THRESHOLD) obs.nearMissChecked = true;
    }
    gd.obstacles = gd.obstacles.filter(o => o.x + o.w > -20);

    // --- Update collectibles ---
    for (let ci = 0; ci < gd.collectibles.length; ci++) {
      const col = gd.collectibles[ci];
      col.x -= gd.speed;
      col.floatOffset += 0.08;

      if (p.magnetTimer > 0 && !col.collected) {
        const dx = (p.x + PLAYER_W / 2) - col.x;
        const dy = (p.y + PLAYER_H / 2) - col.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150) {
          col.x += dx * 0.08;
          col.y += dy * 0.08;
        }
      }
    }
    gd.collectibles = gd.collectibles.filter(c => c.x > -20 && !c.collected);

    // --- Update particles ---
    for (let pi = 0; pi < gd.particles.length; pi++) {
      const part = gd.particles[pi];
      part.x += part.vx;
      part.y += part.vy;
      part.vy += 0.12;
      part.life--;
    }
    gd.particles = gd.particles.filter(p => p.life > 0);
    if (gd.particles.length > 200) gd.particles.splice(0, gd.particles.length - 200);

    // --- Update floating texts ---
    for (let fi = 0; fi < gd.floatingTexts.length; fi++) {
      const ft = gd.floatingTexts[fi];
      ft.y += ft.vy;
      ft.life--;
    }
    gd.floatingTexts = gd.floatingTexts.filter(ft => ft.life > 0);

    // --- Update background ---
    for (let bi = 0; bi < gd.bgElements.length; bi++) {
      const bg = gd.bgElements[bi];
      bg.x -= gd.speed * bg.speed;
      if (bg.x + bg.w < 0) {
        bg.x = CANVAS_W + Math.random() * 100;
        if (bg.type === 'building') {
          bg.h = 60 + Math.random() * 140;
          bg.y = GROUND_Y - bg.h;
          bg.w = 45 + Math.random() * 45;
          bg.windowSeed = Math.floor(Math.random() * 10000);
          bg.hasAntenna = Math.random() > 0.6;
          bg.hasAC = Math.random() > 0.5;
          bg.roofType = Math.floor(Math.random() * 3);
        }
      }
    }

    // --- Update foreground props ---
    for (let gi = 0; gi < gd.fgProps.length; gi++) {
      const fg = gd.fgProps[gi];
      fg.x -= gd.speed * fg.speed;
      if (fg.x < -40) {
        fg.x = CANVAS_W + 50 + Math.random() * 200;
        fg.type = (['plant', 'cooler', 'bin'] as const)[Math.floor(Math.random() * 3)];
      }
    }

    // --- Collision detection ---
    const pH = p.ducking ? PLAYER_H * 0.5 : PLAYER_H;
    const pY = p.ducking ? GROUND_Y - pH : p.y;
    const playerBox = { x: p.x + 6, y: pY + 6, w: PLAYER_W - 12, h: pH - 12 };

    // Obstacles
    for (let oi = 0; oi < gd.obstacles.length; oi++) {
      const obs = gd.obstacles[oi];
      if (obs.passed) continue;
      const obsBox = { x: obs.x + 3, y: obs.y + 3, w: obs.w - 6, h: obs.h - 6 };
      if (
        playerBox.x < obsBox.x + obsBox.w &&
        playerBox.x + playerBox.w > obsBox.x &&
        playerBox.y < obsBox.y + obsBox.h &&
        playerBox.y + playerBox.h > obsBox.y
      ) {
        if (p.invincibleTimer > 0 || p.shieldTimer > 0) {
          obs.passed = true;
          spawnParticles(obs.x + obs.w / 2, obs.y + obs.h / 2, '#FFD600', 10);
          if (p.shieldTimer > 0) p.shieldTimer = 0;
          if (!mutedRef.current) playSound('powerup');
        } else {
          obs.passed = true;
          gd.lives--;
          gd.combo = 0;
          gd.comboTimer = 0;
          p.invincibleTimer = 90;
          gd.screenShake = 15;
          gd.flashTimer = 10;
          spawnParticles(p.x + PLAYER_W / 2, p.y + PLAYER_H / 2, '#FF4444', 15);
          if (!mutedRef.current) playSound('hit');

          if (gd.lives <= 0) {
            gameStateRef.current = 'gameover';
            if (!mutedRef.current) playSound('gameover');
            setGamePhase('gameover');
            setScore(gd.score);
            setDistance(Math.floor(gd.distance));
            setCoins(gd.coins);
            setLives(0);

            const entry: LeaderboardEntry = {
              playerName: playerName || (isSignedIn ? (user?.username || user?.fullName || 'Runner') : 'Guest'),
              score: gd.score + gd.coins * 10,
              distance: Math.floor(gd.distance),
              date: new Date().toISOString(),
            };
            const updated = [...localLeaderboard, entry]
              .sort((a, b) => b.score - a.score)
              .slice(0, 10);
            setLocalLeaderboard(updated);
            localStorage.setItem('orgrun_leaderboard', JSON.stringify(updated));
            if (isSignedIn) {
              submitScoreMutation.mutate(entry);
            }
            return;
          }
        }
      }
    }

    // Collectibles
    for (let ci = 0; ci < gd.collectibles.length; ci++) {
      const col = gd.collectibles[ci];
      if (col.collected) continue;
      const colSize = 20;
      if (
        playerBox.x < col.x + colSize &&
        playerBox.x + playerBox.w > col.x - colSize / 2 &&
        playerBox.y < col.y + colSize &&
        playerBox.y + playerBox.h > col.y - colSize / 2
      ) {
        col.collected = true;
        const config = COLLECTIBLE_CONFIGS[col.type];
        gd.score += config.points;
        if (col.type === 'coin') {
          gd.coins++;
          spawnParticles(col.x, col.y, '#FFD600', 8);
          spawnFloatingText(col.x, col.y - 10, '+10', '#FFD600');
        } else if (col.type === 'coffee') {
          p.coffeeTimer = 300;
          spawnParticles(col.x, col.y, '#8B4513', 8);
          spawnFloatingText(col.x, col.y - 10, 'SPEED BOOST!', '#FF8C00');
        } else if (col.type === 'shield') {
          p.shieldTimer = 600;
          spawnParticles(col.x, col.y, '#0044FF', 8);
          spawnFloatingText(col.x, col.y - 10, 'SHIELD!', '#0044FF');
        } else if (col.type === 'magnet') {
          p.magnetTimer = 480;
          spawnParticles(col.x, col.y, '#FF00FF', 8);
          spawnFloatingText(col.x, col.y - 10, 'MAGNET!', '#FF00FF');
        }
        if (!mutedRef.current) playSound(col.type === 'coin' ? 'coin' : 'powerup');
      }
    }

    // Update React state periodically
    if (gd.frameCount % 10 === 0) {
      setScore(gd.score);
      setDistance(Math.floor(gd.distance));
      setLives(gd.lives);
      setCoins(gd.coins);
    }

    // --- RENDER ---
    ctx.save();

    // Screen shake
    if (gd.screenShake > 0) {
      const shakeX = (Math.random() - 0.5) * gd.screenShake;
      const shakeY = (Math.random() - 0.5) * gd.screenShake;
      ctx.translate(shakeX, shakeY);
    }

    // Sky gradient (richer)
    const skyGrad = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
    skyGrad.addColorStop(0, '#050510');
    skyGrad.addColorStop(0.3, '#0a0a20');
    skyGrad.addColorStop(0.6, '#12122e');
    skyGrad.addColorStop(1, '#1a1a3a');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Stars (twinkling)
    for (let i = 0; i < 40; i++) {
      const sx = ((i * 97 + gd.frameCount * 0.03) % CANVAS_W);
      const sy = (i * 31 + Math.sin(i + gd.frameCount * 0.008) * 3) % (GROUND_Y - 60);
      const twinkle = 0.2 + Math.abs(Math.sin(gd.frameCount * 0.02 + i * 1.7)) * 0.4;
      ctx.fillStyle = `rgba(255,255,255,${twinkle})`;
      const starSize = 1 + (i % 3) * 0.5;
      ctx.fillRect(sx, sy, starSize, starSize);
    }

    // Distant city skyline silhouette (very far parallax, barely moves)
    ctx.fillStyle = 'rgba(15,15,30,0.9)';
    for (let i = 0; i < 20; i++) {
      const bx = ((i * 55 - gd.frameCount * 0.02 * gd.speed) % (CANVAS_W + 60)) - 30;
      const bh = 15 + (i * 37 % 40);
      ctx.fillRect(bx, GROUND_Y - bh, 25 + (i % 3) * 10, bh);
    }

    // Background elements (parallax buildings with details)
    for (const bg of gd.bgElements) {
      if (bg.type === 'building') {
        // Building body
        ctx.fillStyle = bg.color;
        ctx.fillRect(bg.x, bg.y, bg.w, bg.h);

        // Facade panels (subtle horizontal stripes)
        ctx.fillStyle = 'rgba(0,0,0,0.08)';
        for (let sy = bg.y + 4; sy < bg.y + bg.h; sy += 12) {
          ctx.fillRect(bg.x, sy, bg.w, 1);
        }

        // Roof details
        if (bg.roofType === 1) {
          // Pointed roof
          ctx.fillStyle = bg.color;
          ctx.beginPath();
          ctx.moveTo(bg.x, bg.y);
          ctx.lineTo(bg.x + bg.w / 2, bg.y - 12);
          ctx.lineTo(bg.x + bg.w, bg.y);
          ctx.closePath();
          ctx.fill();
        } else if (bg.roofType === 2) {
          // Stepped roof
          ctx.fillStyle = bg.color;
          ctx.fillRect(bg.x + 5, bg.y - 8, bg.w - 10, 8);
          ctx.fillRect(bg.x + 12, bg.y - 14, bg.w - 24, 6);
        }

        // Antenna
        if (bg.hasAntenna) {
          ctx.strokeStyle = 'rgba(255,255,255,0.15)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(bg.x + bg.w / 2, bg.y);
          ctx.lineTo(bg.x + bg.w / 2, bg.y - 18);
          ctx.stroke();
          // Blinking light
          if (gd.frameCount % 60 < 30) {
            ctx.fillStyle = 'rgba(255,0,0,0.6)';
            ctx.beginPath();
            ctx.arc(bg.x + bg.w / 2, bg.y - 18, 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // AC unit
        if (bg.hasAC) {
          ctx.fillStyle = 'rgba(80,80,100,0.6)';
          ctx.fillRect(bg.x + bg.w - 14, bg.y + 15, 10, 8);
        }

        // Windows (varied: yellow, blue-white, dark)
        for (let wy = bg.y + 8; wy < bg.y + bg.h - 10; wy += 14) {
          for (let wx = bg.x + 5; wx < bg.x + bg.w - 8; wx += 11) {
            const seed = bg.windowSeed + Math.floor(wx * 7 + wy * 13);
            const r = ((seed * 9301 + 49297) % 233280) / 233280;
            if (r > 0.25) {
              if (r > 0.7) {
                ctx.fillStyle = 'rgba(255,210,0,0.3)'; // Warm yellow
              } else if (r > 0.5) {
                ctx.fillStyle = 'rgba(150,200,255,0.2)'; // Blue-white
              } else {
                ctx.fillStyle = 'rgba(255,255,255,0.08)'; // Dim
              }
              ctx.fillRect(wx, wy, 6, 8);
            }
          }
        }
      } else if (bg.type === 'cloud') {
        ctx.fillStyle = bg.color;
        // Multi-circle cloud shape
        ctx.beginPath();
        ctx.ellipse(bg.x + bg.w * 0.3, bg.y + bg.h * 0.5, bg.w * 0.3, bg.h * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(bg.x + bg.w * 0.6, bg.y + bg.h * 0.4, bg.w * 0.35, bg.h * 0.55, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(bg.x + bg.w * 0.45, bg.y + bg.h * 0.3, bg.w * 0.25, bg.h * 0.4, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Ground (multi-layer carpet floor)
    // Carpet base
    const groundGrad = ctx.createLinearGradient(0, GROUND_Y, 0, CANVAS_H);
    groundGrad.addColorStop(0, '#3a3548');
    groundGrad.addColorStop(0.15, '#342f42');
    groundGrad.addColorStop(1, '#2a2538');
    ctx.fillStyle = groundGrad;
    ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);

    // Carpet grid pattern
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 1;
    for (let gx = -gd.groundOffset; gx < CANVAS_W; gx += 20) {
      ctx.beginPath();
      ctx.moveTo(gx, GROUND_Y);
      ctx.lineTo(gx, CANVAS_H);
      ctx.stroke();
    }
    for (let gy = GROUND_Y + 10; gy < CANVAS_H; gy += 20) {
      ctx.beginPath();
      ctx.moveTo(0, gy);
      ctx.lineTo(CANVAS_W, gy);
      ctx.stroke();
    }

    // Baseboard strip
    ctx.fillStyle = '#4a4458';
    ctx.fillRect(0, GROUND_Y - 1, CANVAS_W, 5);
    ctx.fillStyle = '#5a546a';
    ctx.fillRect(0, GROUND_Y - 1, CANVAS_W, 2);

    // Ground accent line (blue)
    ctx.strokeStyle = '#0044FF';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y);
    ctx.lineTo(CANVAS_W, GROUND_Y);
    ctx.stroke();

    // Floor marking dashes
    ctx.strokeStyle = 'rgba(0,68,255,0.15)';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 14]);
    ctx.lineDashOffset = -gd.groundOffset;
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y + 18);
    ctx.lineTo(CANVAS_W, GROUND_Y + 18);
    ctx.stroke();
    ctx.setLineDash([]);

    // --- Draw foreground props (behind obstacles) ---
    for (const fg of gd.fgProps) {
      if (fg.x < -30 || fg.x > CANVAS_W + 30) continue;
      ctx.save();
      if (fg.type === 'plant') {
        // Small office plant
        ctx.fillStyle = '#6B4226';
        ctx.fillRect(fg.x - 5, GROUND_Y - 20, 10, 20);
        ctx.fillStyle = '#2d8a4e';
        ctx.beginPath();
        ctx.arc(fg.x, GROUND_Y - 24, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#3ca55c';
        ctx.beginPath();
        ctx.arc(fg.x - 5, GROUND_Y - 20, 7, 0, Math.PI * 2);
        ctx.fill();
      } else if (fg.type === 'cooler') {
        // Water cooler
        ctx.fillStyle = '#aabbcc';
        ctx.fillRect(fg.x - 6, GROUND_Y - 28, 12, 28);
        ctx.fillStyle = '#7799bb';
        ctx.fillRect(fg.x - 4, GROUND_Y - 24, 8, 4);
        // Water jug
        ctx.fillStyle = 'rgba(100,160,220,0.3)';
        ctx.beginPath();
        ctx.ellipse(fg.x, GROUND_Y - 32, 5, 7, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (fg.type === 'bin') {
        // Trash bin
        ctx.fillStyle = '#555';
        ctx.fillRect(fg.x - 7, GROUND_Y - 22, 14, 22);
        ctx.fillStyle = '#666';
        ctx.fillRect(fg.x - 8, GROUND_Y - 22, 16, 3);
      }
      ctx.restore();
    }

    // --- Draw obstacles (canvas-drawn) ---
    for (const obs of gd.obstacles) {
      if (obs.x > CANVAS_W + 10 || obs.x + obs.w < -10) continue;
      drawObstacle(ctx, obs);
    }

    // --- Draw collectibles (canvas-drawn) ---
    for (const col of gd.collectibles) {
      if (col.collected || col.x > CANVAS_W + 10) continue;
      drawCollectible(ctx, col, gd.frameCount);
    }

    // --- Draw player ---
    const drawPlayerH = p.ducking ? PLAYER_H * 0.5 : PLAYER_H;
    const drawPlayerY = p.ducking ? GROUND_Y - drawPlayerH : p.y;

    // Invincibility flash
    const visible = p.invincibleTimer <= 0 || Math.floor(gd.frameCount / 3) % 2 === 0;

    if (visible) {
      // Shield aura
      if (p.shieldTimer > 0) {
        ctx.strokeStyle = 'rgba(0,68,255,0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(p.x + PLAYER_W / 2, drawPlayerY + drawPlayerH / 2, PLAYER_W * 0.9, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = 'rgba(0,68,255,0.06)';
        ctx.fill();
      }

      // Coffee aura
      if (p.coffeeTimer > 0) {
        ctx.strokeStyle = 'rgba(255,140,0,0.4)';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.lineDashOffset = -gd.frameCount;
        ctx.beginPath();
        ctx.arc(p.x + PLAYER_W / 2, drawPlayerY + drawPlayerH / 2, PLAYER_W * 0.85, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Magnet aura
      if (p.magnetTimer > 0) {
        ctx.strokeStyle = 'rgba(255,0,255,0.3)';
        ctx.lineWidth = 1;
        const pulseR = PLAYER_W * 0.9 + Math.sin(gd.frameCount * 0.1) * 5;
        ctx.beginPath();
        ctx.arc(p.x + PLAYER_W / 2, drawPlayerY + drawPlayerH / 2, pulseR, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Draw the actual player character
      drawPlayer(
        ctx, p.x, drawPlayerY, drawPlayerH,
        p.ducking, p.isGrounded, p.runFrame, p.vy,
        p.coffeeTimer, gd.frameCount,
        p.squashStretch
      );
    }

    // --- Speed lines (at high speed) ---
    const speedPercent = Math.min(1, (gd.speed - BASE_SPEED) / (MAX_SPEED - BASE_SPEED));
    if (speedPercent > 0.65) {
      const lineAlpha = (speedPercent - 0.65) * 2;
      ctx.strokeStyle = `rgba(255,255,255,${lineAlpha * 0.12})`;
      ctx.lineWidth = 1;
      for (let i = 0; i < 8; i++) {
        const ly = 30 + ((i * 47 + gd.frameCount * 3) % (GROUND_Y - 40));
        const lx = ((i * 131 + gd.frameCount * gd.speed * 0.8) % (CANVAS_W + 100)) - 50;
        const lLen = 30 + speedPercent * 40;
        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(lx + lLen, ly);
        ctx.stroke();
      }
    }

    // --- Particles ---
    for (const part of gd.particles) {
      const alpha = part.life / part.maxLife;
      ctx.globalAlpha = alpha * 0.8;
      ctx.fillStyle = part.color;
      ctx.beginPath();
      ctx.arc(part.x, part.y, part.size / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // --- Floating texts ---
    for (const ft of gd.floatingTexts) {
      const alpha = ft.life / ft.maxLife;
      const scale = 0.8 + alpha * 0.4;
      ctx.globalAlpha = alpha;
      ctx.font = `bold ${Math.floor(14 * scale)}px "DM Sans", sans-serif`;
      ctx.fillStyle = ft.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(ft.text, ft.x, ft.y);
    }
    ctx.globalAlpha = 1;

    // --- Hit flash ---
    if (gd.flashTimer > 0) {
      ctx.fillStyle = `rgba(255,0,0,${gd.flashTimer / 20})`;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }

    // --- HUD on canvas ---
    // Score
    ctx.font = 'bold 16px "DM Sans", sans-serif';
    ctx.fillStyle = '#FFD600';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`SCORE: ${gd.score}`, 12, 12);

    // Distance
    ctx.fillStyle = '#fff';
    ctx.font = '12px "DM Sans", sans-serif';
    ctx.fillText(`${Math.floor(gd.distance)}m`, 12, 32);

    // Combo counter
    if (gd.combo >= 2) {
      ctx.font = `bold ${12 + Math.min(gd.combo, 10)}px "DM Sans", sans-serif`;
      ctx.fillStyle = `hsl(${280 + gd.combo * 10}, 100%, 65%)`;
      ctx.fillText(`${gd.combo}x COMBO`, 12, 68);
    }

    // Speed indicator
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(12, 48, 80, 8);
    ctx.fillStyle = speedPercent < 0.5 ? '#0044FF' : speedPercent < 0.8 ? '#FFD600' : '#FF4444';
    ctx.fillRect(12, 48, 80 * speedPercent, 8);
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    ctx.strokeRect(12, 48, 80, 8);
    ctx.font = '9px "DM Sans", sans-serif';
    ctx.fillStyle = '#888';
    ctx.fillText('SPEED', 12, 60);

    // Coins (top right)
    ctx.font = 'bold 14px "DM Sans", sans-serif';
    ctx.fillStyle = '#FFD600';
    ctx.textAlign = 'right';
    ctx.fillText(`⭐ ${gd.coins}`, CANVAS_W - 12, 12);

    // Lives
    ctx.textAlign = 'right';
    ctx.font = '14px serif';
    let livesStr = '';
    for (let i = 0; i < gd.lives; i++) livesStr += '❤️';
    ctx.fillText(livesStr, CANVAS_W - 12, 32);

    // Active power-up indicators
    ctx.textAlign = 'center';
    ctx.font = '10px "DM Sans", sans-serif';
    let powerX = CANVAS_W / 2 - 60;
    if (p.shieldTimer > 0) {
      ctx.fillStyle = '#0044FF';
      ctx.fillText(`🛡️ ${Math.ceil(p.shieldTimer / 60)}s`, powerX, 12);
      powerX += 50;
    }
    if (p.coffeeTimer > 0) {
      ctx.fillStyle = '#FF8C00';
      ctx.fillText(`☕ ${Math.ceil(p.coffeeTimer / 60)}s`, powerX, 12);
      powerX += 50;
    }
    if (p.magnetTimer > 0) {
      ctx.fillStyle = '#FF00FF';
      ctx.fillText(`🧲 ${Math.ceil(p.magnetTimer / 60)}s`, powerX, 12);
      powerX += 50;
    }

    ctx.restore();

    // Continue loop
    rafRef.current = requestAnimationFrame(() => gameLoopRef.current());
  }, [spawnObstacle, spawnCollectible, spawnParticles, spawnFloatingText, playerName, localLeaderboard, submitScoreMutation]);

  useEffect(() => {
    gameLoopRef.current = gameLoop;
  }, [gameLoop]);

  // ---------------------------------------------------------------------------
  // Start / Reset
  // ---------------------------------------------------------------------------

  const startGame = useCallback(() => {
    const p = playerRef.current;
    p.x = 100;
    p.y = GROUND_Y - PLAYER_H;
    p.vy = 0;
    p.isGrounded = true;
    p.jumpsLeft = 2;
    p.ducking = false;
    p.shieldTimer = 0;
    p.magnetTimer = 0;
    p.coffeeTimer = 0;
    p.invincibleTimer = 0;
    p.runFrame = 0;
    p.frameCounter = 0;
    p.squashStretch = { sx: 1, sy: 1 };
    p.wasGrounded = true;

    const gd = gameDataRef.current;
    gd.speed = BASE_SPEED;
    gd.score = 0;
    gd.distance = 0;
    gd.coins = 0;
    gd.lives = 3;
    gd.combo = 0;
    gd.comboTimer = 0;
    gd.obstacles = [];
    gd.collectibles = [];
    gd.particles = [];
    gd.floatingTexts = [];
    gd.bgElements = generateBackground();
    gd.fgProps = generateForegroundProps();
    gd.nextObstacleSpawn = 100;
    gd.nextCollectibleSpawn = 60;
    gd.frameCount = 0;
    gd.lastMilestone = 0;
    gd.groundOffset = 0;
    gd.screenShake = 0;
    gd.flashTimer = 0;

    inputRef.current = { jumpPressed: false, duckPressed: false };

    setScore(0);
    setDistance(0);
    setLives(3);
    setCoins(0);
    setScoreSavedToGlobal(false);

    gameStateRef.current = 'playing';
    setGamePhase('playing');

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(gameLoop);
  }, [generateBackground, generateForegroundProps, gameLoop]);

  // ---------------------------------------------------------------------------
  // Input handling
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameStateRef.current === 'menu' && (e.code === 'Space' || e.code === 'Enter')) {
        e.preventDefault();
        startGame();
        return;
      }
      if (gameStateRef.current === 'gameover' && e.code === 'KeyR') {
        startGame();
        return;
      }
      if (gameStateRef.current !== 'playing') return;

      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        e.preventDefault();
        inputRef.current.jumpPressed = true;
      }
      if (e.code === 'ArrowDown' || e.code === 'KeyS') {
        e.preventDefault();
        inputRef.current.duckPressed = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'ArrowDown' || e.code === 'KeyS') {
        inputRef.current.duckPressed = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [startGame]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    if (gameStateRef.current === 'menu') { startGame(); return; }
    if (gameStateRef.current === 'gameover') { startGame(); return; }

    const touch = e.touches[0];
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const relY = y / rect.height;

    if (relY > 0.65) {
      inputRef.current.duckPressed = true;
    } else {
      inputRef.current.jumpPressed = true;
    }
  }, [startGame]);

  const handleTouchEnd = useCallback(() => {
    inputRef.current.duckPressed = false;
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      tryUnlockOrientation();
    };
  }, []);

  // Draw initial menu screen
  useEffect(() => {
    if (gamePhase !== 'menu') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background
    const skyGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    skyGrad.addColorStop(0, '#050510');
    skyGrad.addColorStop(1, '#1a1a3a');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Ground
    const groundGrad = ctx.createLinearGradient(0, GROUND_Y, 0, CANVAS_H);
    groundGrad.addColorStop(0, '#3a3548');
    groundGrad.addColorStop(1, '#2a2538');
    ctx.fillStyle = groundGrad;
    ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
    // Baseboard
    ctx.fillStyle = '#5a546a';
    ctx.fillRect(0, GROUND_Y - 1, CANVAS_W, 3);
    ctx.strokeStyle = '#0044FF';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y);
    ctx.lineTo(CANVAS_W, GROUND_Y);
    ctx.stroke();

    // Menu buildings
    const menuBldgs = [
      { x: 40, w: 55, h: 100 }, { x: 110, w: 40, h: 130 },
      { x: 170, w: 60, h: 80 }, { x: 600, w: 50, h: 110 },
      { x: 660, w: 45, h: 90 }, { x: 720, w: 55, h: 140 },
    ];
    for (const b of menuBldgs) {
      ctx.fillStyle = `hsl(225, 15%, ${20 + Math.random() * 8}%)`;
      ctx.fillRect(b.x, GROUND_Y - b.h, b.w, b.h);
      ctx.fillStyle = 'rgba(255,210,0,0.25)';
      for (let wy = GROUND_Y - b.h + 8; wy < GROUND_Y - 8; wy += 14) {
        for (let wx = b.x + 5; wx < b.x + b.w - 6; wx += 11) {
          if (Math.random() > 0.35) ctx.fillRect(wx, wy, 5, 7);
        }
      }
    }

    // Title
    ctx.font = 'bold 48px "DM Sans", sans-serif';
    ctx.fillStyle = '#FFD600';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('ORG RUN', CANVAS_W / 2, 85);

    ctx.font = '15px "DM Sans", sans-serif';
    ctx.fillStyle = '#888';
    ctx.fillText('Endless Runner • Dodge the Bureaucracy', CANVAS_W / 2, 120);

    // Draw preview character
    drawPlayer(
      ctx, CANVAS_W / 2 - PLAYER_W / 2, GROUND_Y - PLAYER_H,
      PLAYER_H, false, true, 0, 0, 0, 0, { sx: 1, sy: 1 }
    );

    // Draw preview obstacles
    const previewObs: Obstacle[] = [
      { x: CANVAS_W / 2 + 70, y: GROUND_Y - 42, w: 34, h: 42, type: 'paperwork', passed: false, nearMissChecked: false },
      { x: CANVAS_W / 2 + 130, y: GROUND_Y - 38, w: 44, h: 38, type: 'meeting', passed: false, nearMissChecked: false },
    ];
    for (const obs of previewObs) drawObstacle(ctx, obs);

    // Draw preview collectible
    drawCollectible(ctx, { x: CANVAS_W / 2 + 100, y: GROUND_Y - 70, type: 'coin', collected: false, floatOffset: 0 }, 0);

    // Instructions
    ctx.font = 'bold 18px "DM Sans", sans-serif';
    ctx.fillStyle = '#0044FF';
    ctx.fillText('PRESS SPACE OR TAP TO START', CANVAS_W / 2, 210);

    ctx.font = '12px "DM Sans", sans-serif';
    ctx.fillStyle = '#666';
    ctx.fillText('⬆️ / SPACE = Jump  •  ⬇️ = Duck  •  Double-Jump Available!', CANVAS_W / 2, 240);

    // Feature pills
    ctx.font = '10px "DM Sans", sans-serif';
    const features = ['⭐ COINS', '☕ SPEED', '🛡️ SHIELD', '🧲 MAGNET', '🔥 COMBOS'];
    features.forEach((f, i) => {
      const fx = CANVAS_W / 2 - 175 + i * 75;
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(fx, 260, 65, 18);
      ctx.fillStyle = '#777';
      ctx.textAlign = 'center';
      ctx.fillText(f, fx + 32, 272);
    });
  }, [gamePhase]);

  // Fullscreen
  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      await el.requestFullscreen?.().catch(() => {});
      if (isMobileDevice()) await tryLockLandscape();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen?.().catch(() => {});
      tryUnlockOrientation();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const onFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="w-full max-w-4xl mx-auto" ref={containerRef}>
      {/* Portrait mobile warning */}
      {isPortraitMobile && (
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center text-white gap-4 p-8">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
            <path d="M12 18h.01" />
            <path d="M2 12 C2 6 6 2 12 2" />
            <polyline points="8 2 12 2 12 6" />
          </svg>
          <p className="text-lg font-bold text-center">Rotate your device to landscape</p>
          <p className="text-sm text-gray-400 text-center">OrgRun plays best in landscape orientation</p>
        </div>
      )}

      {/* Header bar */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-black text-white tracking-tight">ORG RUN</h2>
          <span className="text-xs text-gray-500 font-mono tracking-wider">ENDLESS RUNNER</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLeaderboard(true)}
            className="p-2 rounded hover:bg-white/10 text-yellow-400 transition-colors"
            title="Leaderboard"
          >
            <Trophy size={18} />
          </button>
          <button
            onClick={() => setMuted(!muted)}
            className="p-2 rounded hover:bg-white/10 text-gray-400 transition-colors"
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded hover:bg-white/10 text-gray-400 transition-colors"
            title="Fullscreen"
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative border-2 border-white/10 rounded-lg overflow-hidden bg-black">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="w-full block"
          style={{ imageRendering: 'pixelated' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        />

        {/* Game Over overlay */}
        <AnimatePresence>
          {gamePhase === 'gameover' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4"
            >
              <motion.div
                initial={{ scale: 0.5, y: -20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="text-center"
              >
                <h3 className="text-3xl font-black text-red-500 mb-2 tracking-tight">GAME OVER</h3>
                <div className="grid grid-cols-3 gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-400">{score}</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-blue-400">{Math.floor(distance)}m</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Distance</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-300">⭐ {coins}</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Coins</div>
                  </div>
                </div>

                {/* Name input / Sign In CTA */}
                {isSignedIn ? (
                  <div className="mb-4">
                    <input
                      type="text"
                      value={playerName}
                      onChange={(e) => {
                        const name = e.target.value.slice(0, 12);
                        setPlayerName(name);
                        localStorage.setItem('orgrun_player_name', name);
                      }}
                      placeholder="Your name..."
                      className="bg-white/10 border border-white/20 text-white text-center px-4 py-2 rounded text-sm w-48 placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                    />
                    <div className="text-[11px] text-emerald-400 font-mono mt-1">
                      {scoreSavedToGlobal ? '✓ Score recorded on global leaderboard' : submitScoreMutation.isPending ? 'Saving score to global leaderboard...' : '✓ High score saved'}
                    </div>
                  </div>
                ) : (
                  <div className="bg-black/80 border border-yellow-400/50 p-3 rounded max-w-xs mx-auto mb-4 text-center">
                    <p className="text-[11px] text-gray-300 font-mono mb-1">Score saved to local device.</p>
                    <p className="text-xs font-bold text-white mb-2">Sign in to save your score to the global leaderboard.</p>
                    <Link
                      to="/sign-in"
                      className="inline-block w-full py-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold uppercase text-xs tracking-wider transition-colors rounded"
                    >
                      Sign In to Save Score
                    </Link>
                  </div>
                )}

                <div className="flex gap-3 justify-center">
                  <button
                    onClick={startGame}
                    className="flex items-center gap-2 bg-[#0044FF] hover:bg-blue-700 text-white font-bold py-3 px-6 transition-colors uppercase tracking-wider text-sm"
                  >
                    <RotateCcw size={16} />
                    Play Again
                  </button>
                  <button
                    onClick={() => setShowLeaderboard(true)}
                    className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 transition-colors uppercase tracking-wider text-sm"
                  >
                    <Trophy size={16} />
                    Scores
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">Press R to restart</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Live HUD below canvas */}
      {gamePhase === 'playing' && (
        <div className="flex items-center justify-between mt-2 px-2 text-sm">
          <div className="flex items-center gap-4">
            <span className="text-yellow-400 font-bold font-mono">{score} pts</span>
            <span className="text-gray-400 font-mono">{Math.floor(distance)}m</span>
          </div>
          <div className="flex items-center gap-1">
            {Array.from({ length: lives }).map((_, i) => (
              <Heart key={i} size={14} className="text-red-500 fill-red-500" />
            ))}
          </div>
          <span className="text-yellow-300 font-mono">⭐ {coins}</span>
        </div>
      )}

      {/* Controls hint */}
      {gamePhase === 'menu' && (
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-400">
          <div className="border border-white/10 rounded p-3 bg-white/5">
            <div className="font-bold text-white mb-1">🎮 Controls</div>
            <div>⬆️ / Space / W = Jump</div>
            <div>⬇️ / S = Duck</div>
            <div>Double-tap to double-jump!</div>
          </div>
          <div className="border border-white/10 rounded p-3 bg-white/5">
            <div className="font-bold text-white mb-1">🎯 Goal</div>
            <div>Dodge paperwork, meetings, audits</div>
            <div>Collect ⭐ coins & power-ups</div>
            <div>Build combos • Score near-misses!</div>
          </div>
        </div>
      )}

      {/* Mobile touch buttons */}
      {isMobileDevice() && gamePhase === 'playing' && (
        <div className="flex gap-2 mt-3 justify-center">
          <button
            onTouchStart={(e) => { e.preventDefault(); inputRef.current.jumpPressed = true; }}
            className="bg-white/10 active:bg-white/20 border border-white/20 text-white font-bold py-4 px-8 rounded-lg text-lg select-none touch-none"
          >
            ⬆️ JUMP
          </button>
          <button
            onTouchStart={(e) => { e.preventDefault(); inputRef.current.duckPressed = true; }}
            onTouchEnd={() => { inputRef.current.duckPressed = false; }}
            className="bg-white/10 active:bg-white/20 border border-white/20 text-white font-bold py-4 px-8 rounded-lg text-lg select-none touch-none"
          >
            ⬇️ DUCK
          </button>
        </div>
      )}

      {/* Leaderboard Modal */}
      <AnimatePresence>
        {showLeaderboard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            onClick={() => setShowLeaderboard(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#111] border-2 border-yellow-400/30 rounded-xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-black text-yellow-400 flex items-center gap-2">
                  <Trophy size={20} /> LEADERBOARD
                </h3>
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="p-1 hover:bg-white/10 rounded text-gray-400"
                >
                  ✕
                </button>
              </div>

              {hasActiveNode && (
                <GameLeaderboardScopeTabs
                  scope={leaderboardScope}
                  onScopeChange={setLeaderboardScope}
                  nodeName={nodeName}
                />
              )}
              {leaderboard.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No scores yet. Be the first!</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {leaderboard.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between py-2 px-3 rounded ${
                        i === 0 ? 'bg-yellow-400/10 border border-yellow-400/30' :
                        i === 1 ? 'bg-gray-300/5 border border-gray-400/20' :
                        i === 2 ? 'bg-orange-400/5 border border-orange-400/20' :
                        'bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-lg font-black w-6 text-center ${
                          i === 0 ? 'text-yellow-400' :
                          i === 1 ? 'text-gray-300' :
                          i === 2 ? 'text-orange-400' : 'text-gray-500'
                        }`}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                        </span>
                        <span className="text-white font-bold text-sm">{entry.playerName}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-yellow-400 font-bold text-sm">{entry.score.toLocaleString()}</div>
                        <div className="text-gray-500 text-[10px]">{entry.distance?.toLocaleString()}m</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
